/**
 * SearchWorkspacePage — /search route for Workspace Shell V1 (Step 2)
 * 
 * Purpose: Help users find new literature and save it into LitHub.
 * UX Rule: Search is for discovery and import, not deep consumption.
 * 
 * Layout:
 * - SearchSidebar (left): search controls, quick chips, filters, recent searches
 * - Main workspace: search results list
 * - SearchPreviewDrawer (right): article preview and quick save actions
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { useWorkspace } from '../../components/workspace/WorkspaceShell';
import { SearchSidebar } from '../../components/workspace/search/SearchSidebar';
import { SearchResultRow, SearchArticle } from '../../components/workspace/search/SearchResultRow';
import { SearchPreviewDrawer } from '../../components/workspace/search/SearchPreviewDrawer';
import { FolderSelectDialog } from '../../components/workspace/FolderSelectDialog';
import { LITARCHIVE_PATH, LITHUB_SHELF_PATH } from '../../config/featureNav';
import { articlesAPI, libraryAPI, preferencesAPI } from '../../api';
import { toast } from 'sonner';
import { trackPageView, trackSearch, trackSaveFromSearch } from '../../utils/analytics';
import {
  Search,
  Sparkles,
  BookOpen,
  SlidersHorizontal,
  TrendingUp,
} from 'lucide-react';

// Local storage key for recent searches
const RECENT_SEARCHES_KEY = 'litpulse_recent_searches';
const MAX_RECENT_SEARCHES = 10;

interface SearchFilters {
  dateRange: string;
  studyDesign: string;
  journal: string;
}

const SearchWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshBootstrap } = useWorkspace();

  // State
  const [query, setQuery] = React.useState(searchParams.get('q') || '');
  const [filters, setFilters] = React.useState<SearchFilters>({
    dateRange: searchParams.get('dateRange') || '365',
    studyDesign: searchParams.get('design') || 'all',
    journal: searchParams.get('journal') || '',
  });
  const [results, setResults] = React.useState<SearchArticle[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  
  // Saved articles tracking
  const [savedPmids, setSavedPmids] = React.useState<Set<string>>(new Set());
  const [savingPmid, setSavingPmid] = React.useState<string | null>(null);
  
  // Folder selection dialog state
  const [folderDialogOpen, setFolderDialogOpen] = React.useState(false);
  const [pendingSaveArticle, setPendingSaveArticle] = React.useState<SearchArticle | null>(null);
  
  // Preview drawer state
  const [selectedArticle, setSelectedArticle] = React.useState<SearchArticle | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  
  // Recent searches
  const [recentSearches, setRecentSearches] = React.useState<string[]>([]);

  // Preference-based topic chips for empty state
  const [preferences, setPreferences] = React.useState<any>(null);

  // Load recent searches and preferences on mount
  React.useEffect(() => {
    // Track page view
    trackPageView('search');
    
    // Load recent searches from localStorage
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load recent searches:', e);
    }

    // Load preferences for empty state chips
    const loadPrefs = async () => {
      try {
        const prefs = await preferencesAPI.getMyPreferences();
        setPreferences(prefs);
      } catch (e) {
        console.error('Failed to load preferences:', e);
      }
    };
    loadPrefs();
  }, []);

  // Load user's saved articles to track save state
  React.useEffect(() => {
    const loadSavedArticles = async () => {
      try {
        const response = await libraryAPI.getLibrary({ limit: 500 });
        const pmids = new Set((response.articles || []).map((a: any) => a.pmid).filter(Boolean));
        setSavedPmids(pmids as Set<string>);
      } catch (e) {
        console.error('Failed to load saved articles:', e);
      }
    };
    loadSavedArticles();
  }, []);

  // Auto-search if query param exists
  React.useEffect(() => {
    const q = searchParams.get('q');
    if (q && !hasSearched) {
      handleSearch();
    }
  }, [searchParams]);

  // Update URL params when search is performed
  const updateUrlParams = (newQuery: string, newFilters: SearchFilters) => {
    const params = new URLSearchParams();
    if (newQuery) params.set('q', newQuery);
    if (newFilters.dateRange !== '365') params.set('dateRange', newFilters.dateRange);
    if (newFilters.studyDesign !== 'all') params.set('design', newFilters.studyDesign);
    if (newFilters.journal) params.set('journal', newFilters.journal);
    setSearchParams(params, { replace: true });
  };

  // Add to recent searches
  const addToRecentSearches = (searchQuery: string) => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    
    const updated = [trimmed, ...recentSearches.filter(s => s !== trimmed)].slice(0, MAX_RECENT_SEARCHES);
    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  };

  // Handle search — tries V2 first, falls back to V1 if V2 is unavailable
  const handleSearch = async () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setResults([]);
    updateUrlParams(trimmedQuery, filters);
    addToRecentSearches(trimmedQuery);

    try {
      // Build study types array from filter
      const studyTypes = filters.studyDesign !== 'all' ? [filters.studyDesign] : undefined;
      
      // Calculate lookback years from date range
      const lookbackYears = filters.dateRange === 'all' 
        ? undefined 
        : Math.ceil(parseInt(filters.dateRange) / 365) || 1;

      let articles: SearchArticle[] = [];

      try {
        // Primary: Search V2 with bucketed results and preference context
        const response = await articlesAPI.searchV2({
          query: trimmedQuery,
          use_preferences_context: false,
          study_types: studyTypes,
          lookback_years: lookbackYears,
          limit: 50,
        });
        articles = response.articles || [];
      } catch (v2Error: any) {
        // Fallback: If V2 is unavailable (disabled, 404, 500), try V1 search
        console.warn('Search V2 unavailable, falling back to V1:', v2Error?.response?.status);
        try {
          const v1Response = await articlesAPI.search(trimmedQuery, {
            date_range_days: lookbackYears ? lookbackYears * 365 : undefined,
          });
          articles = (v1Response as any).articles || [];
        } catch (v1Error: any) {
          // Both search endpoints failed
          console.error('Both V1 and V2 search failed:', v1Error);
          throw v1Error;
        }
      }

      // Filter by journal if specified
      let filteredArticles = articles;
      if (filters.journal.trim()) {
        const journalFilter = filters.journal.toLowerCase();
        filteredArticles = articles.filter((a: SearchArticle) => 
          a.journal?.toLowerCase().includes(journalFilter)
        );
      }

      setResults(filteredArticles);
      
      // Track search analytics
      trackSearch(trimmedQuery, filters, filteredArticles.length);

      if (filteredArticles.length === 0) {
        toast.info('No articles found. Try different search terms or adjust filters.');
      } else {
        toast.success(`Found ${filteredArticles.length} articles`);
      }
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.detail?.message || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle save to library - opens folder selection dialog
  const handleSaveToLibrary = async (article: SearchArticle) => {
    if (!article.pmid) return;
    
    // Open folder selection dialog
    setPendingSaveArticle(article);
    setFolderDialogOpen(true);
  };

  // Handle actual save after folder selection
  const handleConfirmSave = async (folder: string) => {
    if (!pendingSaveArticle?.pmid) return;
    
    setSavingPmid(pendingSaveArticle.pmid);
    try {
      await libraryAPI.saveArticle({
        pmid: pendingSaveArticle.pmid,
        folder,
        title: pendingSaveArticle.title,
        journal: pendingSaveArticle.journal,
        source: 'search',
      });
      
      // Update saved state
      setSavedPmids(prev => new Set([...prev, pendingSaveArticle.pmid]));
      
      // Track save analytics
      trackSaveFromSearch(pendingSaveArticle.pmid, folder);
      
      toast.success(`Saved to ${folder}!`, {
        description: pendingSaveArticle.title.substring(0, 50) + '...',
        action: {
          label: 'View in LitHub',
          onClick: () => navigate(LITHUB_SHELF_PATH),
        },
      });
      
      refreshBootstrap();
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.info('Article is already in your LitHub');
        setSavedPmids(prev => new Set([...prev, pendingSaveArticle.pmid]));
      } else {
        toast.error('Failed to save article');
      }
    } finally {
      setSavingPmid(null);
      setPendingSaveArticle(null);
    }
  };

  const handleAddToFolderForSaved = (article: SearchArticle) => {
    setPendingSaveArticle(article);
    setFolderDialogOpen(true);
  };

  // Handle article selection for preview
  const handleSelectArticle = (article: SearchArticle) => {
    setSelectedArticle(article);
    setPreviewOpen(true);
  };

  // Handle recent search click
  const handleRecentSearchClick = (searchQuery: string) => {
    setQuery(searchQuery);
    // Trigger search after state update
    setTimeout(() => handleSearch(), 100);
  };

  // Get quick topic chips for empty state
  const quickChips = React.useMemo(() => {
    const chips: string[] = [];
    if (preferences?.specialty_name) chips.push(preferences.specialty_name);
    if (preferences?.subspecialty_name) chips.push(preferences.subspecialty_name);
    if (preferences?.custom_keywords) chips.push(...preferences.custom_keywords.slice(0, 3));
    return chips.length > 0 ? chips : ['heart failure', 'diabetes management', 'COVID-19 treatment'];
  }, [preferences]);

  return (
    <div className="h-full flex" data-testid="search-workspace-page">
      {/* Desktop search sidebar */}
      <SearchSidebar
        query={query}
        onQueryChange={setQuery}
        onSearch={handleSearch}
        filters={filters}
        onFiltersChange={setFilters}
        recentSearches={recentSearches}
        onRecentSearchClick={handleRecentSearchClick}
        onClearRecentSearches={clearRecentSearches}
        isSearching={loading}
      />

      {/* Main Results Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile search controls */}
        <div className="md:hidden border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                placeholder="Search PubMed…"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm bg-white"
                data-testid="search-mobile-input"
              />
            </div>
            <Button
              size="sm"
              className="h-10 shrink-0"
              onClick={() => handleSearch()}
              disabled={loading}
            >
              Search
            </Button>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" aria-label="Filters">
                  <SlidersHorizontal className="w-4 h-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[min(300px,90vw)] p-0">
                <SheetHeader className="px-4 py-3 border-b">
                  <SheetTitle className="text-sm">Search & filters</SheetTitle>
                </SheetHeader>
                <div className="h-[calc(100%-3rem)] overflow-hidden">
                  <SearchSidebar
                    query={query}
                    onQueryChange={setQuery}
                    onSearch={handleSearch}
                    filters={filters}
                    onFiltersChange={setFilters}
                    recentSearches={recentSearches}
                    onRecentSearchClick={handleRecentSearchClick}
                    onClearRecentSearches={clearRecentSearches}
                    isSearching={loading}
                    variant="drawer"
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Results Header */}
        {hasSearched && (
          <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-sm font-medium text-[hsl(var(--foreground))]">
                {loading ? 'Searching...' : `${results.length} results`}
              </h2>
              {query && !loading && (
                <Badge variant="secondary" className="text-xs truncate max-w-[200px]">
                  "{query}"
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Results List */}
        <ScrollArea className="flex-1">
          <div className="p-4">
            <div className="max-w-3xl mx-auto">
              {loading ? (
                // Loading skeleton
                <div className="space-y-3" data-testid="search-loading">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="p-3 border border-[hsl(var(--border))] rounded-lg">
                      <Skeleton className="h-5 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/3 mb-2" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  ))}
                </div>
              ) : results.length > 0 ? (
                // Results list
                <div className="space-y-2" data-testid="search-results">
                  {results.map((article) => (
                    <SearchResultRow
                      key={article.pmid}
                      article={article}
                      isSaved={savedPmids.has(article.pmid)}
                      isSaving={savingPmid === article.pmid}
                      isSelected={selectedArticle?.pmid === article.pmid}
                      onSelect={() => handleSelectArticle(article)}
                      onSave={() => handleSaveToLibrary(article)}
                      onAddToFolder={
                        savedPmids.has(article.pmid)
                          ? () => handleAddToFolderForSaved(article)
                          : undefined
                      }
                      onOpenInLitHub={() => navigate(LITHUB_SHELF_PATH)}
                      onOpenInLitArchive={() => navigate(LITARCHIVE_PATH)}
                    />
                  ))}
                </div>
              ) : hasSearched ? (
                // No results
                <div className="text-center py-16" data-testid="search-no-results">
                  <Search className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4 opacity-50" />
                  <h3 className="font-medium text-[hsl(var(--foreground))] mb-2">No results found</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Try different search terms or adjust your filters in the sidebar.
                  </p>
                </div>
              ) : (
                // Empty state - no search yet
                <div className="py-12" data-testid="search-empty">
                  <div className="text-center max-w-lg mx-auto">
                    <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--primary))]/10 flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-[hsl(var(--primary))]" />
                    </div>
                    <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">
                      Search for Literature
                    </h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                      Find articles from PubMed and save them to your LitHub for later reading.
                    </p>

                    {/* Quick topic chips */}
                    <div className="mb-6">
                      <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-3">
                        Quick searches
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {quickChips.map((chip, i) => (
                          <Badge
                            key={i}
                            variant="secondary"
                            className="cursor-pointer hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] transition-colors px-3 py-1"
                            onClick={() => {
                              setQuery(chip);
                              setTimeout(() => handleSearch(), 100);
                            }}
                            data-testid={`quick-chip-${i}`}
                          >
                            {chip}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Recent searches in empty state */}
                    {recentSearches.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-3">
                          Recent searches
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {recentSearches.slice(0, 3).map((search, i) => (
                            <Badge
                              key={i}
                              variant="outline"
                              className="cursor-pointer hover:bg-[hsl(var(--muted))] transition-colors px-3 py-1"
                              onClick={() => handleRecentSearchClick(search)}
                            >
                              {search}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 max-w-xl mx-auto">
                    <div className="p-4 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]">
                      <Sparkles className="w-5 h-5 text-[hsl(var(--primary))] mb-2" />
                      <h3 className="text-sm font-medium text-[hsl(var(--foreground))] mb-1">
                        AI-Powered Summaries
                      </h3>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        Save articles to get AI-generated summaries and insights.
                      </p>
                    </div>
                    <div className="p-4 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--card))]">
                      <BookOpen className="w-5 h-5 text-[hsl(var(--accent))] mb-2" />
                      <h3 className="text-sm font-medium text-[hsl(var(--foreground))] mb-1">
                        Build Your LitHub
                      </h3>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        Curate your personal knowledge base of medical literature.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Preview Drawer */}
      <SearchPreviewDrawer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        article={selectedArticle}
        isSaved={selectedArticle ? savedPmids.has(selectedArticle.pmid) : false}
        isSaving={savingPmid === selectedArticle?.pmid}
        onSave={() => selectedArticle && handleSaveToLibrary(selectedArticle)}
        onOpenInLearn={() => navigate('/library/shelf')}
      />

      {/* Folder Selection Dialog */}
      <FolderSelectDialog
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        onSelect={handleConfirmSave}
        articleTitle={pendingSaveArticle?.title}
      />
    </div>
  );
};

export default SearchWorkspacePage;
